import { LeaderboardEntry } from '@src/data/types';
import { LeaderboardReward } from 'jigsaw';
import {
  HStack,
  NumberTicker,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from 'ui_2';

const MEDALS = ['🥇', '🥈', '🥉'];

type LeaderboardProps = {
  entries: LeaderboardEntry[];
  isLoading: boolean;
  userEntry?: LeaderboardEntry;
  reward?: LeaderboardReward;
};

type LeaderboardRowProps = {
  entry: LeaderboardEntry;
  isUserEntry: boolean;
};

type LeaderboardBodyProps = {
  entries: LeaderboardEntry[];
  isLoading: boolean;
  userEntry?: LeaderboardEntry;
  reward?: LeaderboardReward;
};

// Header component
const LeaderboardHeader = () => (
  <TableHeader>
    <TableRow>
      <TableHead className='w-10 text-center'>#</TableHead>
      <TableHead>User</TableHead>
      <TableHead className='text-right'>Score</TableHead>
    </TableRow>
  </TableHeader>
);

// Individual row component
const LeaderboardRow = ({ entry, isUserEntry }: LeaderboardRowProps) => {
  let medalIdx = entry.rank - 1;
  const hasMedal = medalIdx >= 0 && medalIdx < 3;

  return (
    <TableRow
      key={entry.primaryAccount + entry.rank}
      className={`transition-colors duration-300 hover:bg-gray-50 hover:bg-opacity-50 ${
        isUserEntry ? 'bg-yellow-100 font-bold' : ''
      }`}
    >
      <TableCell className='text-center'>
        {hasMedal ? (
          <span className='text-xl'>{MEDALS[medalIdx]}</span>
        ) : (
          <Text font='bold-md' color='gray-600'>
            {entry.rank}
          </Text>
        )}
      </TableCell>
      <TableCell>
        <HStack className='items-center gap-3'>
          <Text
            font={hasMedal ? 'bold-sm' : 'medium-sm'}
            className='max-w-[160px] truncate'
          >
            {entry.primaryAccount.length > 16
              ? `${entry.primaryAccount.slice(
                  0,
                  8
                )}...${entry.primaryAccount.slice(-4)}`
              : entry.primaryAccount}
          </Text>
          {isUserEntry && (
            <Text className='rounded-full bg-yellow-100 px-2' font='sm'>
              YOU
            </Text>
          )}
        </HStack>
      </TableCell>
      <TableCell className='text-right'>
        <NumberTicker value={entry.totalAmount} />
      </TableCell>
    </TableRow>
  );
};

// Body component
const LeaderboardBody = ({
  entries,
  isLoading,
  userEntry,
  reward,
}: LeaderboardBodyProps) => {
  // Check if current user is in the entries array
  const isUserInEntries =
    userEntry &&
    entries.some((entry) => entry.primaryAccount === userEntry.primaryAccount);

  // If user is not in entries and has an entry, add it at the bottom
  const displayEntries = [...entries];
  if (userEntry && !isUserInEntries) {
    displayEntries.push(userEntry);
  }

  if (isLoading) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={3} className='text-center'>
            Loading...
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  if (!displayEntries.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={3} className='text-center'>
            No data
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {displayEntries.map((entry) => {
        const isUserEntry = Boolean(
          userEntry && entry.primaryAccount === userEntry.primaryAccount
        );

        return (
          <LeaderboardRow
            key={entry.primaryAccount + entry.rank}
            entry={entry}
            isUserEntry={isUserEntry}
          />
        );
      })}
    </TableBody>
  );
};

// Main leaderboard component
export const Leaderboard = ({
  entries,
  isLoading,
  userEntry,
  reward,
}: LeaderboardProps) => {
  return (
    <div className='w-full'>
      <Table>
        <LeaderboardHeader />
        <LeaderboardBody
          entries={entries}
          isLoading={isLoading}
          userEntry={userEntry}
          reward={reward}
        />
      </Table>
    </div>
  );
};
